/**
 * Pure state machine for the board -> mini-game transition (docs/concept/board/06-minimal-transition.png,
 * docs/concept/board/05-minigame-transition.md). Four stages, always in this order, and each only ever
 * advances for ONE reason:
 *
 *   reveal    -> briefing   after a fixed on-screen delay. Nothing to click.
 *   briefing  -> ready      the instant the LOCAL participant presses Ready.
 *   ready     -> countdown  automatically, the instant every participant is ready (readyCount >= totalPlayers).
 *   countdown -> starting   automatically, one tick per second, down to zero.
 *
 * There is no event that moves a stage backwards, and no "start" event: the countdown only ever begins as a
 * side effect of everyone being ready, and `starting` is terminal — 05-minigame-transition.md: "There is no
 * second Start action." This module is the only place that logic lives; `useBoardTransitionStage.ts` wires
 * timers to it and `BoardTransition.tsx` only ever sees the resulting stage, never a transition.
 */

export type BoardTransitionStage = 'reveal' | 'briefing' | 'ready' | 'countdown' | 'starting';

export interface BoardTransitionStageState {
  stage: BoardTransitionStage;
  /** Whole seconds remaining. Set the instant `countdown` begins, decremented on each tick. Null otherwise. */
  countdownValue: number | null;
}

export type BoardTransitionStageEvent =
  | { type: 'reveal_elapsed' }
  | { type: 'self_ready' }
  | { type: 'ready_count_changed'; readyCount: number }
  | { type: 'countdown_tick' };

export interface BoardTransitionStageConfig {
  /** Everyone who must be ready before the countdown can start. */
  totalPlayers: number;
  countdownSeconds: number;
}

export function initialBoardTransitionStageState(): BoardTransitionStageState {
  return { stage: 'reveal', countdownValue: null };
}

/**
 * The whole machine. Deliberately exhaustive over `event.type` with a `default` fallthrough so an unknown or
 * stray event (e.g. a duplicate/late message) is always a no-op rather than a way to force a transition.
 */
export function boardTransitionStageReducer(
  state: BoardTransitionStageState,
  event: BoardTransitionStageEvent,
  config: BoardTransitionStageConfig,
): BoardTransitionStageState {
  switch (event.type) {
    case 'reveal_elapsed':
      return state.stage === 'reveal' ? { stage: 'briefing', countdownValue: null } : state;

    case 'self_ready':
      return state.stage === 'briefing' ? { stage: 'ready', countdownValue: null } : state;

    case 'ready_count_changed': {
      // Only ever consulted while waiting on the roster; once counting down (or done), a late/duplicate
      // readiness message must not restart or re-arm anything — this is the "no second Start" guarantee.
      if (state.stage !== 'ready') return state;
      if (config.totalPlayers <= 0 || event.readyCount < config.totalPlayers) return state;
      return { stage: 'countdown', countdownValue: Math.max(0, config.countdownSeconds) };
    }

    case 'countdown_tick': {
      if (state.stage !== 'countdown' || state.countdownValue === null) return state;
      const next = state.countdownValue - 1;
      return next <= 0 ? { stage: 'starting', countdownValue: 0 } : { stage: 'countdown', countdownValue: next };
    }

    default:
      return state;
  }
}

export function isTransitionComplete(stage: BoardTransitionStage): boolean {
  return stage === 'starting';
}
